import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, X, Loader2, CheckCircle, GraduationCap,
  ToggleLeft, ToggleRight, AlertCircle, GripVertical, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Grammar', 'Vocabulary', 'Reading'];
const OPTION_LABELS = ['A', 'B', 'C', 'D'];

const EMPTY_FORM = {
  text: '',
  category: 'Grammar',
  options: ['', '', '', ''],
  correct_answer_index: 0,
  sort_order: 0,
};

const CategoryBadge = ({ category }) => {
  const styles = {
    Grammar:    'bg-[#1A2E6E]/10 text-[#1A2E6E]',
    Vocabulary: 'bg-[#4CAF50]/10 text-[#4CAF50]',
    Reading:    'bg-[#F5A623]/10 text-[#C97D0A]',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[category] || 'bg-gray-100 text-gray-600'}`}>
      {category}
    </span>
  );
};

// ─── Question Form Modal ──────────────────────────────────────────────────────
const QuestionModal = ({ question, totalQuestions, onClose, onSaved }) => {
  const isEditing = !!question;
  const [form, setForm] = useState(
    isEditing
      ? {
          text: question.text,
          category: question.category,
          options: [...question.options],
          correct_answer_index: question.correct_answer_index,
          sort_order: question.sort_order ?? 0,
        }
      : { ...EMPTY_FORM, sort_order: totalQuestions }
  );
  const [saving, setSaving] = useState(false);

  const setOption = (idx, val) => {
    const next = [...form.options];
    next[idx] = val;
    setForm(f => ({ ...f, options: next }));
  };

  const handleSave = async () => {
    if (!form.text.trim()) { toast.error('Question text is required'); return; }
    if (form.options.some(o => !o.trim())) { toast.error('All 4 options are required'); return; }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/admin/level-test-questions/${question.id}`, form);
        toast.success('Question updated');
      } else {
        await axios.post(`${API_URL}/api/admin/level-test-questions`, form);
        toast.success('Question created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
          <h2 className="text-lg font-bold text-[#1A2E6E]">
            {isEditing ? 'Edit Question' : 'New Question'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
            <X size={20} className="text-[#1A2E6E]/60" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          {/* Question text */}
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Question *</label>
            <textarea
              rows={3}
              value={form.text}
              onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
              placeholder='e.g. She ___ to the gym every morning.'
              className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none text-sm"
            />
          </div>

          {/* Category + order */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Category *</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white text-sm"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Order</label>
              <input
                type="number"
                min="0"
                value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none text-sm"
              />
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
              Answer Options * <span className="text-[#1A2E6E]/40 font-normal">(click the letter to mark as correct)</span>
            </label>
            <div className="space-y-2">
              {form.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, correct_answer_index: idx }))}
                    title={form.correct_answer_index === idx ? 'Correct answer' : 'Mark as correct'}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                      form.correct_answer_index === idx
                        ? 'border-[#4CAF50] bg-[#4CAF50] text-white'
                        : 'border-[#D1D5DB] text-[#9CA3AF] hover:border-[#4CAF50]/60'
                    }`}
                  >
                    {OPTION_LABELS[idx]}
                  </button>
                  <input
                    type="text"
                    value={opt}
                    onChange={e => setOption(idx, e.target.value)}
                    placeholder={`Option ${OPTION_LABELS[idx]}`}
                    className={`flex-1 px-3 py-2.5 rounded-xl border text-sm outline-none transition-colors ${
                      form.correct_answer_index === idx
                        ? 'border-[#4CAF50]/50 bg-[#4CAF50]/3'
                        : 'border-[#D1D5DB] focus:border-[#1A2E6E]'
                    }`}
                  />
                  {form.correct_answer_index === idx && (
                    <CheckCircle size={16} className="text-[#4CAF50] shrink-0" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-[#1A2E6E]/40 mt-2">
              Green = correct answer
            </p>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-[#E8EAF0]">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#F5F7FA] font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-[#1A2E6E] text-white hover:bg-[#16275C] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
            {isEditing ? 'Save Changes' : 'Create Question'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const AdminLevelTestQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [togglingId, setTogglingId]   = useState(null);

  useEffect(() => { fetchQuestions(); }, []);

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/level-test-questions?page_size=200`);
      setQuestions(res.data.data ?? res.data);
    } catch {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditingQuestion(null); setModalOpen(true); };
  const openEdit   = (q)  => { setEditingQuestion(q);  setModalOpen(true); };

  const handleDelete = async (q) => {
    if (!window.confirm(`Delete question "${q.text.slice(0, 60)}…"?`)) return;
    setDeletingId(q.id);
    try {
      await axios.delete(`${API_URL}/api/admin/level-test-questions/${q.id}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch {
      toast.error('Failed to delete question');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = async (q) => {
    setTogglingId(q.id);
    try {
      await axios.put(`${API_URL}/api/admin/level-test-questions/${q.id}`, { is_active: !q.is_active });
      toast.success(q.is_active ? 'Question deactivated' : 'Question activated');
      fetchQuestions();
    } catch {
      toast.error('Failed to update question');
    } finally {
      setTogglingId(null);
    }
  };

  const moveQuestion = async (q, direction) => {
    const idx = questions.findIndex(x => x.id === q.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= questions.length) return;

    const sibling = questions[swapIdx];
    try {
      await Promise.all([
        axios.put(`${API_URL}/api/admin/level-test-questions/${q.id}`,       { sort_order: sibling.sort_order }),
        axios.put(`${API_URL}/api/admin/level-test-questions/${sibling.id}`, { sort_order: q.sort_order }),
      ]);
      fetchQuestions();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  const activeCount = questions.filter(q => q.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Level Test Questions</h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Manage the questions shown in the English level assessment
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium shrink-0"
        >
          <Plus size={16} /> New Question
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total', value: questions.length, color: 'text-[#1A2E6E]' },
          { label: 'Active', value: activeCount, color: 'text-[#4CAF50]' },
          { label: 'Inactive', value: questions.length - activeCount, color: 'text-[#F5A623]' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-[#E8EAF0] p-5">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-sm text-[#1A2E6E]/60">{label}</p>
          </div>
        ))}
      </div>

      {activeCount === 0 && (
        <div className="flex items-start gap-3 p-4 bg-[#F5A623]/5 border border-[#F5A623]/20 rounded-xl">
          <AlertCircle className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" />
          <p className="text-sm text-[#1A2E6E]/70">
            No active questions — the level test will use the built-in default questions.
            Add at least one active question to override the defaults.
          </p>
        </div>
      )}

      {/* Questions list */}
      {questions.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <GraduationCap className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-4" />
          <p className="text-[#1A2E6E]/60">No custom questions yet. The test uses built-in defaults.</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#1A2E6E] text-white rounded-xl text-sm font-medium hover:bg-[#16275C] transition-colors"
          >
            <Plus size={14} /> Add first question
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="divide-y divide-[#E8EAF0]">
            {questions.map((q, i) => (
              <motion.div
                key={q.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`p-5 transition-colors ${!q.is_active ? 'opacity-50 bg-[#F8F9FC]' : 'hover:bg-[#F8F9FC]/50'}`}
              >
                <div className="flex items-start gap-4">
                  {/* Order controls */}
                  <div className="flex flex-col items-center gap-0.5 shrink-0 mt-0.5">
                    <button
                      onClick={() => moveQuestion(q, 'up')}
                      disabled={i === 0}
                      className="p-1 text-[#1A2E6E]/30 hover:text-[#1A2E6E] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <span className="text-xs font-mono text-[#1A2E6E]/40 leading-none">{i + 1}</span>
                    <button
                      onClick={() => moveQuestion(q, 'down')}
                      disabled={i === questions.length - 1}
                      className="p-1 text-[#1A2E6E]/30 hover:text-[#1A2E6E] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <CategoryBadge category={q.category} />
                      {!q.is_active && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#F5F5F5] text-[#1A2E6E]/40">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-[#1A2E6E] text-sm leading-snug mb-3">{q.text}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {q.options.map((opt, oi) => (
                        <div
                          key={oi}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                            oi === q.correct_answer_index
                              ? 'bg-[#4CAF50]/10 text-[#2E7D32] font-semibold border border-[#4CAF50]/20'
                              : 'bg-[#F8F9FC] text-[#1A2E6E]/60'
                          }`}
                        >
                          <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                            oi === q.correct_answer_index ? 'bg-[#4CAF50] text-white' : 'bg-[#E8EAF0] text-[#9CA3AF]'
                          }`}>
                            {OPTION_LABELS[oi]}
                          </span>
                          {opt}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(q)}
                      className="p-2 rounded-lg text-[#1A2E6E]/50 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleToggle(q)}
                      disabled={togglingId === q.id}
                      title={q.is_active ? 'Deactivate' : 'Activate'}
                      className={`p-2 rounded-lg transition-colors ${
                        q.is_active
                          ? 'text-[#4CAF50] hover:bg-[#4CAF50]/10'
                          : 'text-[#1A2E6E]/30 hover:bg-[#1A2E6E]/5'
                      }`}
                    >
                      {togglingId === q.id
                        ? <Loader2 size={17} className="animate-spin" />
                        : q.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />
                      }
                    </button>
                    <button
                      onClick={() => handleDelete(q)}
                      disabled={deletingId === q.id}
                      className="p-2 rounded-lg text-[#D72B2B]/50 hover:text-[#D72B2B] hover:bg-[#D72B2B]/5 transition-colors"
                      title="Delete"
                    >
                      {deletingId === q.id
                        ? <Loader2 size={15} className="animate-spin" />
                        : <Trash2 size={15} />
                      }
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {modalOpen && (
          <QuestionModal
            question={editingQuestion}
            totalQuestions={questions.length}
            onClose={() => setModalOpen(false)}
            onSaved={() => { setModalOpen(false); fetchQuestions(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminLevelTestQuestions;
